import { useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink } from "react-router-dom";
import logo from "../../logo copy/Beta_v0.01.png";
import LanguageSwitcher from "../../../components/misc/LanguageSwitcher";
import { LanguageContext } from "../../../context/LanguageContext";

export default function Navbar() {
  const { t, isRTL } = useContext(LanguageContext);

  const [openMobile, setOpenMobile] = useState(false);
  const [openMega, setOpenMega] = useState(false);

  const links = t("navbar.links", { returnObjects: true }) || [];
  const megaSections = t("navbar.megaMenu.sections", { returnObjects: true }) || [];

  const MotionNavLink = motion(NavLink);

  return (
    <motion.nav
      dir={isRTL ? "rtl" : "ltr"}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full border-b border-gray-200 bg-white relative z-50"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">

        {/* LOGO */}
        <NavLink to="/" className="flex items-center">
          <img src={logo} alt="Teqa logo" className="w-24" />
        </NavLink>

        {/* DESKTOP LINKS */}
        <div className="hidden md:flex items-center space-x-8 text-gray-700 font-medium relative">

          {/* Normal links */}
          {links.map((item) => (
            <MotionNavLink
              key={item.path}
              to={item.path}
              whileHover={{ y: -2 }}
              className={({ isActive }) =>
                `transition hover:text-indigo-600 ${
                  isActive ? "text-indigo-600 font-semibold" : ""
                }`
              }
            >
              {item.label}
            </MotionNavLink>
          ))}

          {/* MEGA MENU */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMega(true)}
            onMouseLeave={() => setOpenMega(false)}
          >
            <span className="cursor-pointer hover:text-indigo-600">
              {t("navbar.megaMenu.title")}
            </span>

            <AnimatePresence>
              {openMega && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 top-full mt-6 w-[640px] -translate-x-1/2 rounded-2xl border bg-white p-8 shadow-xl"
                >
                  <div className="grid grid-cols-2 gap-10">
                    {megaSections.map((section, i) => (
                      <div key={i}>
                        <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-900">
                          {section.title}
                        </h4>

                        <ul className="space-y-3">
                          {section.links.map((link, j) => (
                            <NavLink
                              key={j}
                              to={link.path}
                              className="block text-gray-600 hover:text-indigo-600"
                            >
                              {link.label}
                            </NavLink>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* DESKTOP ACTIONS */}
        <div className="hidden md:flex items-center space-x-6">
          <LanguageSwitcher />

          <NavLink
            to="/login"
            className="font-medium text-gray-700 hover:text-indigo-600"
          >
            {t("navbar.auth.login")}
          </NavLink>

          <motion.a
            whileHover={{ scale: 1.05 }}
            href="/register"
            className="rounded-lg bg-indigo-600 px-5 py-2 font-semibold text-white"
          >
            {t("navbar.auth.signup")}
          </motion.a>
        </div>

        {/* MOBILE HAMBURGER */}
        <button
          onClick={() => setOpenMobile(!openMobile)}
          className="md:hidden flex flex-col gap-1"
        >
          <span className="h-0.5 w-6 bg-gray-800"></span>
          <span className="h-0.5 w-6 bg-gray-800"></span>
          <span className="h-0.5 w-6 bg-gray-800"></span>
        </button>
      </div>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {openMobile && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t bg-white"
          >
            <div className="flex flex-col gap-6 px-6 py-6">

              {/* Links */}
              {links.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpenMobile(false)}
                  className="text-gray-700 font-medium"
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Mega menu links (mobile) */}
              <div className="pt-4">
                <p className="mb-3 text-sm font-semibold text-gray-500">
                  {t("navbar.megaMenu.title")}
                </p>

                {megaSections.flatMap((section) =>
                  section.links.map((link) => (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      onClick={() => setOpenMobile(false)}
                      className="block py-2 text-gray-700"
                    >
                      {link.label}
                    </NavLink>
                  ))
                )}
              </div>

              <LanguageSwitcher />

              {/* Actions */}
              <div className="flex flex-col gap-3 pt-4">
                <NavLink
                  to="/login"
                  className="rounded-lg border border-gray-300 py-3 text-center font-semibold text-gray-800"
                >
                  {t("navbar.auth.login")}
                </NavLink>

                <NavLink
                  to="/register"
                  className="rounded-lg bg-indigo-600 py-3 text-center font-semibold text-white"
                >
                  {t("navbar.auth.getStarted")}
                </NavLink>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
